import {
  ClientEventReportDTO,
  ClientEventReportFilter,
  PaymentCheckReportFilter,
  PaymentCheckReportResponse,
} from "@/types/reports";
import { axiosInstance } from "@/pages/auth/auth.context";

// API service for reports
export const reportService = {
  /**
   * Fetch client event report data
   * @param filters - Filter criteria for the report
   * @returns Promise containing the report data
   */
  async fetchClientEventReport(
    filters: ClientEventReportFilter,
  ): Promise<ClientEventReportDTO[]> {
    try {
      // Convert filters to query parameters
      const params = new URLSearchParams();

      // Handle array parameters
      if (filters.eventStatuses && filters.eventStatuses.length > 0) {
        filters.eventStatuses.forEach((status) => {
          params.append("eventStatuses", status);
        });
      }

      if (filters.clientIds && filters.clientIds.length > 0) {
        filters.clientIds.forEach((id) => {
          params.append("clientIds", id);
        });
      }

      // Handle date parameters - convert to OffsetDateTime format
      if (filters.eventDateFrom) {
        // Convert YYYY-MM-DD to ISO datetime at start of day
        const fromDate = new Date(filters.eventDateFrom + "T00:00:00");
        params.append("eventDateFrom", fromDate.toISOString());
      }
      if (filters.eventDateTo) {
        // Convert YYYY-MM-DD to ISO datetime at end of day
        const toDate = new Date(filters.eventDateTo + "T23:59:59");
        params.append("eventDateTo", toDate.toISOString());
      }
      if (filters.createdDateFrom) {
        const fromDate = new Date(filters.createdDateFrom + "T00:00:00");
        params.append("createdDateFrom", fromDate.toISOString());
      }
      if (filters.createdDateTo) {
        const toDate = new Date(filters.createdDateTo + "T23:59:59");
        params.append("createdDateTo", toDate.toISOString());
      }

      // Handle string search parameters
      if (filters.clientNameSearch) {
        params.append("clientNameSearch", filters.clientNameSearch);
      }
      if (filters.companySearch) {
        params.append("companySearch", filters.companySearch);
      }
      if (filters.locationSearch) {
        params.append("locationSearch", filters.locationSearch);
      }

      const response = await axiosInstance.get<ClientEventReportDTO[]>(
        `reports/client-events?${params.toString()}`,
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching client event report:", error);
      throw error; // Re-throw the error so the UI can handle it appropriately
    }
  },

  /**
   * Export client event report data
   * @param filters - Filter criteria used to generate the report
   * @param format - Export format ('csv', 'xlsx', etc.)
   */
  async exportClientEventReport(
    filters: ClientEventReportFilter,
  ): Promise<void> {
    try {
      // Convert filters to query parameters (same logic as fetchClientEventReport)
      const params = new URLSearchParams();

      // Handle array parameters
      if (filters.eventStatuses && filters.eventStatuses.length > 0) {
        filters.eventStatuses.forEach((status) => {
          params.append("eventStatuses", status);
        });
      }

      if (filters.clientIds && filters.clientIds.length > 0) {
        filters.clientIds.forEach((id) => {
          params.append("clientIds", id);
        });
      }

      // Handle date parameters - convert to OffsetDateTime format
      if (filters.eventDateFrom) {
        // Convert YYYY-MM-DD to ISO datetime at start of day
        const fromDate = new Date(filters.eventDateFrom + "T00:00:00");
        params.append("eventDateFrom", fromDate.toISOString());
      }
      if (filters.eventDateTo) {
        // Convert YYYY-MM-DD to ISO datetime at end of day
        const toDate = new Date(filters.eventDateTo + "T23:59:59");
        params.append("eventDateTo", toDate.toISOString());
      }
      if (filters.createdDateFrom) {
        const fromDate = new Date(filters.createdDateFrom + "T00:00:00");
        params.append("createdDateFrom", fromDate.toISOString());
      }
      if (filters.createdDateTo) {
        const toDate = new Date(filters.createdDateTo + "T23:59:59");
        params.append("createdDateTo", toDate.toISOString());
      }

      // Handle string search parameters
      if (filters.clientNameSearch) {
        params.append("clientNameSearch", filters.clientNameSearch);
      }
      if (filters.companySearch) {
        params.append("companySearch", filters.companySearch);
      }
      if (filters.locationSearch) {
        params.append("locationSearch", filters.locationSearch);
      }

      const response = await axiosInstance.get<Blob>(
        `reports/client-events/export?${params.toString()}`,
        {
          responseType: "blob", // Important for file downloads
        },
      );

      // Extract filename from Content-Disposition header if present (robust for spaces, commas, quotes)
      const disposition =
        response.headers && response.headers["content-disposition"];
      let filename = `client-event-report-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      if (disposition) {
        // Try to match filename with or without quotes, and handle RFC 5987 encoding
        const filenameRegex =
          /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i;
        const matches = filenameRegex.exec(disposition);
        if (matches) {
          filename = decodeURIComponent(matches[1] || matches[2]);
        }
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting report:", error);
      throw error; // Re-throw to let the UI handle the error
    }
  },

  /**
   * Fetch payment check report data with pagination
   * @param filters - Filter criteria for the report
   * @param page - Page number (0-based)
   * @param size - Page size
   * @param sort - Sort criteria in format "property,direction"
   * @returns Promise containing the paginated report data
   */
  async fetchPaymentCheckReport(
    filters: PaymentCheckReportFilter,
    page: number = 0,
    size: number = 20,
    sort: string = "checkNumber,desc",
  ): Promise<PaymentCheckReportResponse> {
    try {
      const params = new URLSearchParams();

      // Pagination parameters
      params.append("page", page.toString());
      params.append("size", size.toString());
      params.append("sort", sort);

      // Handle filter parameters
      if (filters.recipientNameSearch) {
        params.append("recipientNameSearch", filters.recipientNameSearch);
      }

      if (filters.checkNumber !== undefined) {
        params.append("checkNumber", filters.checkNumber.toString());
      }

      // Handle date parameters - expects ISO DateTime strings (conversion happens in the caller)
      if (filters.issueDateFrom) {
        params.append("issueDateFrom", filters.issueDateFrom);
      }
      if (filters.issueDateTo) {
        params.append("issueDateTo", filters.issueDateTo);
      }
      if (filters.createdDateFrom) {
        params.append("createdDateFrom", filters.createdDateFrom);
      }
      if (filters.createdDateTo) {
        params.append("createdDateTo", filters.createdDateTo);
      }

      // Handle status array
      if (filters.statuses && filters.statuses.length > 0) {
        filters.statuses.forEach((status) => {
          params.append("statuses", status);
        });
      }

      const response = await axiosInstance.get<PaymentCheckReportResponse>(
        `reports/payment-checks?${params.toString()}`,
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching payment check report:", error);
      throw error;
    }
  },

  /**
   * Export payment check report data
   * @param filters - Filter criteria used to generate the report
   */
  async exportPaymentCheckReport(
    filters: PaymentCheckReportFilter,
  ): Promise<void> {
    try {
      const params = new URLSearchParams();

      // Handle filter parameters (same as fetchPaymentCheckReport)
      if (filters.recipientNameSearch) {
        params.append("recipientNameSearch", filters.recipientNameSearch);
      }

      if (filters.checkNumber !== undefined) {
        params.append("checkNumber", filters.checkNumber.toString());
      }

      if (filters.issueDateFrom) {
        params.append("issueDateFrom", filters.issueDateFrom);
      }
      if (filters.issueDateTo) {
        params.append("issueDateTo", filters.issueDateTo);
      }
      if (filters.createdDateFrom) {
        params.append("createdDateFrom", filters.createdDateFrom);
      }
      if (filters.createdDateTo) {
        params.append("createdDateTo", filters.createdDateTo);
      }

      if (filters.statuses && filters.statuses.length > 0) {
        filters.statuses.forEach((status) => {
          params.append("statuses", status);
        });
      }

      const response = await axiosInstance.get<Blob>(
        `reports/payment-checks/export?${params.toString()}`,
        {
          responseType: "blob",
        },
      );

      const disposition =
        response.headers && response.headers["content-disposition"];
      let filename = `payment-check-report-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      if (disposition) {
        const filenameRegex =
          /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i;
        const matches = filenameRegex.exec(disposition);
        if (matches) {
          filename = decodeURIComponent(matches[1] || matches[2]);
        }
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting payment check report:", error);
      throw error;
    }
  },
};
